import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import api from '../services/api';

const ClinicianAcknowledgement = () => {
  const { user, refreshUser, logout } = useAuth();
  const navigate = useNavigate();
  const [acknowledged, setAcknowledged] = useState(false);
  const [hasScrolled, setHasScrolled] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [showDeclineModal, setShowDeclineModal] = useState(false);

  useEffect(() => {
    // Redirect if not a clinician
    if (user?.role !== 'clinician') {
      navigate('/dashboard');
      return;
    }

    // Redirect if already acknowledged
    if (user?.clinicianAcknowledgement?.acknowledged) {
      navigate('/dashboard');
    }
  }, [user, navigate]);

  const handleScroll = (e) => {
    const bottom = e.target.scrollHeight - e.target.scrollTop <= e.target.clientHeight + 50;
    if (bottom && !hasScrolled) {
      setHasScrolled(true);
    }
  };

  const handleAcknowledge = async () => {
    if (!hasScrolled) {
      setError('Please scroll to the bottom of the document before acknowledging.');
      return;
    }

    if (!acknowledged) {
      setError('Please check the acknowledgement box to continue.');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      await api.post('/auth/acknowledge-guidelines', {
        documentVersion: '1.0'
      });

      // Refresh user data
      await refreshUser();

      // Navigate to dashboard
      navigate('/dashboard');
    } catch (err) {
      console.error('Error acknowledging guidelines:', err);
      setError(err.response?.data?.message || 'Failed to acknowledge guidelines. Please try again.');
      setLoading(false);
    }
  };

  const handleDecline = () => {
    setShowDeclineModal(true);
  };

  const confirmDecline = () => {
    // Logout the user and redirect to login page
    logout();
    navigate('/login');
  };

  const cancelDecline = () => {
    setShowDeclineModal(false);
  };

  return (
    <div className="min-h-screen bg-gray-50 py-8 px-4">
      <div className="max-w-5xl mx-auto bg-white rounded-lg shadow-xl">
        {/* Header */}
        <div className="bg-gradient-to-r from-blue-600 to-purple-600 text-white p-8 rounded-t-lg">
          <h1 className="text-3xl font-bold mb-2">Clinician Operations Rules & Guidelines</h1>
          <p className="text-blue-100">MHDF Momentum Platform</p>
          <div className="mt-4 flex items-center gap-4 text-sm">
            <span className="bg-white/20 px-3 py-1 rounded">Version 1.0</span>
            <span className="bg-white/20 px-3 py-1 rounded">February 2026</span>
            <span className="bg-red-500 px-3 py-1 rounded font-semibold">Required Acknowledgment</span>
          </div>
        </div>

        {/* Content */}
        <div 
          className="p-8 max-h-[60vh] overflow-y-auto prose prose-sm max-w-none"
          onScroll={handleScroll}
        >
          <div className="bg-yellow-50 border-l-4 border-yellow-400 p-4 mb-6">
            <p className="font-semibold text-yellow-800">
              ⚠️ IMPORTANT: Please read this entire document carefully before acknowledging. You must scroll to the bottom to enable the acknowledgement checkbox.
            </p>
          </div>

          <h2>Introduction & Purpose</h2>
          <p>
            This document establishes <strong>mandatory operational guidelines</strong> for all licensed clinicians using the MHDF Momentum platform. It defines:
          </p>
          <ul>
            <li>How you can access and use client data</li>
            <li>What constitutes appropriate clinical practice within Momentum</li>
            <li>Your obligations under HIPAA, state law, and professional licensing boards</li>
            <li>Platform-specific rules designed to protect clients and clinical integrity</li>
            <li>Your responsibilities as a referring/supervising clinician</li>
          </ul>

          <h3>What This Platform Is NOT</h3>
          <p>Momentum is <strong>not</strong>:</p>
          <ul>
            <li>A replacement for direct clinical care</li>
            <li>A crisis response system</li>
            <li>A real-time monitoring platform</li>
            <li>A telehealth treatment delivery system</li>
            <li>An electronic health record (EHR)</li>
          </ul>

          <h3>What This Platform IS</h3>
          <p>Momentum is:</p>
          <ul>
            <li>A <strong>clinical supplement</strong> to your in-person or telehealth therapy</li>
            <li>A <strong>structured accountability tool</strong> that extends therapeutic reach between sessions</li>
            <li>A <strong>data source</strong> for treatment planning and progress monitoring</li>
            <li>A <strong>collaboration tool</strong> for clinician-client teams working toward mental wellness</li>
          </ul>

          <h2>Governance & Regulatory Compliance</h2>
          
          <h3>Guiding Principle: Compliance First</h3>
          <p className="font-semibold">
            You must comply with ALL applicable laws, regulations, and ethical standards. Momentum does not replace or reduce your obligations. It supplements them.
          </p>

          <h3>State Licensing Board Requirements</h3>
          <p>
            <strong>Your Primary Obligation:</strong> Follow all rules, regulations, and ethical codes issued by your state licensing board. Your state board may require specific documentation standards, limitations on technology use in treatment, requirements for informed consent, rules about data storage and security, and procedures for crisis response and mandatory reporting.
          </p>

          <h3>HIPAA Compliance</h3>
          <p>You are bound by HIPAA as a covered entity. When using Momentum:</p>
          <ul>
            <li>You are accessing Protected Health Information (PHI)</li>
            <li>Every data access is logged in the audit trail</li>
            <li>You may not share client data without explicit written consent</li>
            <li>You must maintain appropriate security measures when accessing the platform</li>
          </ul>

          <h2>Data Access & Privacy Obligations</h2>
          
          <h3>What You CAN Access (with client consent)</h3>
          <ul>
            <li>✅ Daily completion rates (Mind, Move, Journal pillars)</li>
            <li>✅ Streak counts and consistency patterns</li>
            <li>✅ Phase progression and milestone dates</li>
            <li>✅ Self-reported mood and wellness scores (aggregate trends)</li>
            <li>✅ Journal entry titles and dates (NOT content unless shared)</li>
          </ul>

          <h3>What You CANNOT Access</h3>
          <ul>
            <li>❌ Raw journal entries (only titles/dates, unless client shares)</li>
            <li>❌ Client's password or login credentials</li>
            <li>❌ Real-time activity monitoring (this is not surveillance)</li>
          </ul>

          <h3>Consent-Gating Mechanics</h3>
          <p>Client consent is required for:</p>
          <ul>
            <li>Clinician to view engagement data</li>
            <li>Clinician to receive notifications about client progress</li>
            <li>Clinician to send encouragement messages</li>
            <li>Clinician to view monthly assessments</li>
          </ul>
          <p><strong>Clients can revoke consent at any time without notifying you and without penalty.</strong></p>

          <h2>Scope of Practice & Clinical Boundaries</h2>
          
          <h3>Momentum Is NOT Therapy; You Remain Responsible</h3>
          <p>You remain fully responsible for:</p>
          <ul>
            <li>Initial and ongoing clinical assessment</li>
            <li>Diagnosis and treatment planning</li>
            <li>Medication management (if applicable)</li>
            <li>Crisis response and safety planning</li>
            <li>Ethical decision-making</li>
            <li>Documentation in your primary records</li>
          </ul>

          <h3>Appropriate Uses of Momentum Data</h3>
          <p><strong>✅ Progress Monitoring:</strong> "I see you've been consistent with your daily work—that's real commitment."</p>
          <p><strong>✅ Identifying Patterns:</strong> "I notice you tend to have lower mood scores on Mondays. Let's explore that."</p>
          <p><strong>✅ Treatment Planning:</strong> "Since Momentum shows you're responding well to the Move pillar, let's build more physical activity into your plan."</p>

          <h3>Inappropriate Uses</h3>
          <p><strong>❌ Using Momentum Instead of Assessment:</strong> Don't diagnose based on mood/wellness scores alone.</p>
          <p><strong>❌ Replacing Direct Conversation:</strong> Don't message via Momentum what should be discussed in session.</p>
          <p><strong>❌ Accessing Data Without Purpose:</strong> Don't browse client dashboards out of curiosity.</p>

          <h2>Encouragement Messaging System Guidelines</h2>
          
          <h3>What Messages ARE</h3>
          <ul>
            <li>✅ Brief acknowledgments of observable progress (streaks, completions, phase transitions)</li>
            <li>✅ Encouragement and validation of effort</li>
            <li>✅ One-way communication (clients read but do not reply)</li>
            <li>✅ Template-based only (no free-text messaging)</li>
          </ul>

          <h3>What Messages ARE NOT</h3>
          <ul>
            <li>❌ Treatment directives or clinical instructions</li>
            <li>❌ Crisis management or safety planning</li>
            <li>❌ Conversation or back-and-forth dialogue</li>
            <li>❌ References to private journal content</li>
          </ul>

          <h3>Strict Messaging Rules</h3>
          <ul>
            <li><strong>Template-only:</strong> You cannot free-text messages. All messages use pre-approved templates.</li>
            <li><strong>Rate limit:</strong> Maximum 1 message per client per week.</li>
            <li><strong>Audit trail:</strong> Every message is permanently logged and monitored.</li>
            <li><strong>Client consent required:</strong> Clients must opt-in and can disable messages anytime.</li>
          </ul>

          <h2>Crisis Management & Safety Protocols</h2>
          
          <h3>Momentum Is NOT for Crisis Response</h3>
          <p className="font-semibold text-red-600">
            If you become aware of a client in crisis through Momentum data or any other means, you must follow your established crisis protocols immediately. Do not use Momentum messaging for crisis intervention.
          </p>

          <h3>Your Responsibilities</h3>
          <ul>
            <li>Maintain standard crisis response procedures with all clients</li>
            <li>Provide clients with crisis hotline numbers (988, local resources)</li>
            <li>Document all crisis interventions in your primary clinical records</li>
            <li>Follow mandatory reporting requirements for your jurisdiction</li>
          </ul>

          <h2>Ethical Standards & Professional Conduct</h2>
          
          <h3>Professional Boundaries</h3>
          <ul>
            <li>Maintain appropriate therapeutic boundaries at all times</li>
            <li>Do not use the platform for personal communication</li>
            <li>Do not engage in dual relationships through the platform</li>
            <li>Respect client autonomy regarding data sharing preferences</li>
          </ul>

          <h3>Confidentiality</h3>
          <ul>
            <li>Do not discuss client Momentum data with third parties without consent</li>
            <li>Do not screenshot or share client data from the platform</li>
            <li>Access the platform only from secure, private locations</li>
            <li>Log out when finished; do not leave sessions unattended</li>
          </ul>

          <h2>Prohibited Conduct</h2>
          
          <p>The following actions are strictly prohibited and may result in account suspension or termination:</p>
          <ul>
            <li>❌ Accessing client data without legitimate clinical purpose</li>
            <li>❌ Sharing login credentials with others</li>
            <li>❌ Attempting to access data of clients not assigned to you</li>
            <li>❌ Using the platform to provide emergency or crisis services</li>
            <li>❌ Sending messages that violate template boundaries</li>
            <li>❌ Pressuring clients to grant data access or consent</li>
            <li>❌ Using platform for billing, scheduling, or administrative functions</li>
          </ul>

          <h2>Compliance Monitoring & Audits</h2>
          
          <p>MHDF Momentum actively monitors clinician platform use for compliance. This includes:</p>
          <ul>
            <li>Automated flagging of unusual access patterns</li>
            <li>Message content review for boundary violations</li>
            <li>Audit log analysis for inappropriate data access</li>
            <li>Client complaint investigation</li>
          </ul>

          <h3>Consequences of Non-Compliance</h3>
          <ul>
            <li><strong>First violation:</strong> Warning and mandatory compliance training</li>
            <li><strong>Second violation:</strong> Temporary account suspension (30 days)</li>
            <li><strong>Serious violation:</strong> Immediate termination and report to licensing board if applicable</li>
          </ul>

          <h2>Support & Escalation</h2>
          
          <p>For questions or concerns:</p>
          <ul>
            <li><strong>Technical support:</strong> support@mhdfmomentum.com</li>
            <li><strong>Compliance questions:</strong> compliance@mhdfmomentum.com</li>
            <li><strong>Client safety concerns:</strong> Report through platform + follow standard protocols</li>
          </ul>

          <h2>Final Statement</h2>
          
          <p className="font-semibold">
            The MHDF Momentum platform is a powerful tool for extending clinical support to clients in their mental wellness journey. When used appropriately within the boundaries outlined in this document, it enhances treatment and demonstrates your commitment to your clients' wellbeing.
          </p>
          
          <p className="font-semibold">
            However, it is not a substitute for your clinical judgment, professional responsibility, or ethical commitment. You remain the primary clinician. You make the treatment decisions. You respond to crises. You maintain the therapeutic relationship.
          </p>
          
          <p className="font-semibold text-lg">
            Use this platform thoughtfully. Respect your clients. Follow the law. And remember: your primary obligation is always to the person sitting across from you in session.
          </p>

          <div className="mt-8 p-6 bg-blue-50 border-2 border-blue-200 rounded-lg">
            <p className="font-semibold text-blue-900">
              ✓ You have reached the end of the document.
            </p>
            <p className="text-sm text-blue-800 mt-2">
              You may now enable the acknowledgement checkbox below to continue.
            </p>
          </div>
        </div>

        {/* Acknowledgement Section */}
        <div className="border-t-2 border-gray-200 p-8 bg-gray-50">
          <div className="max-w-3xl mx-auto">
            <div className="flex items-start mb-6">
              <input
                type="checkbox"
                id="acknowledgement"
                checked={acknowledged}
                onChange={(e) => setAcknowledged(e.target.checked)}
                disabled={!hasScrolled}
                className="w-5 h-5 text-blue-600 border-gray-300 rounded focus:ring-blue-500 mt-1 disabled:opacity-50"
              />
              <label
                htmlFor="acknowledgement"
                className={`ml-3 text-sm ${hasScrolled ? 'text-gray-900' : 'text-gray-400'}`}
              >
                <span className="font-semibold">I hereby acknowledge that:</span>
                <ul className="mt-2 space-y-1">
                  <li>• I have read and understood all content in this document</li>
                  <li>• I agree to comply with all rules, guidelines, and boundaries outlined herein</li>
                  <li>• I understand my obligations under HIPAA, state law, and professional ethics</li>
                  <li>• I acknowledge that this platform is a supplement, not a replacement, for my clinical responsibilities</li>
                  <li>• I accept that non-compliance may result in account suspension and/or reporting to licensing authorities</li>
                  <li>• My acknowledgement is legally binding and will be recorded with my IP address and timestamp</li>
                </ul>
              </label>
            </div>

            {error && (
              <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">
                {error}
              </div>
            )}

            <div className="space-y-3">
              <button
                onClick={handleAcknowledge}
                disabled={!acknowledged || !hasScrolled || loading}
                className="w-full bg-blue-600 text-white py-4 rounded-lg font-semibold text-lg hover:bg-blue-700 transition-colors disabled:bg-gray-300 disabled:cursor-not-allowed"
              >
                {loading ? 'Processing...' : 'I Acknowledge and Accept These Guidelines'}
              </button>

              <button
                onClick={handleDecline}
                disabled={loading}
                className="w-full bg-white text-gray-700 py-3 rounded-lg font-medium text-base border-2 border-gray-300 hover:bg-gray-50 hover:border-gray-400 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Decline and Logout
              </button>
            </div>

            <p className="text-xs text-gray-500 text-center mt-4">
              Access to the MHDF Momentum clinician dashboard will be granted immediately upon acknowledgement.
              Your acknowledgement will be logged in the audit trail with timestamp and IP address.
            </p>
          </div>
        </div>
      </div>

      {/* Decline Confirmation Modal */}
      {showDeclineModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-2xl max-w-md w-full">
            <div className="p-6">
              <div className="flex items-center gap-3 mb-4">
                <div className="flex-shrink-0 w-12 h-12 bg-red-100 rounded-full flex items-center justify-center">
                  <svg className="w-6 h-6 text-red-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                  </svg>
                </div>
                <h3 className="text-xl font-bold text-gray-900">Decline Acknowledgement?</h3>
              </div>

              <div className="mb-6">
                <p className="text-gray-700 mb-3">
                  If you decline to acknowledge these guidelines, you will be logged out and will <strong>not have access</strong> to the MHDF Momentum clinician platform functions, including:
                </p>
                <ul className="text-sm text-gray-600 space-y-2 ml-4">
                  <li>• Viewing client progress and engagement data</li>
                  <li>• Generating referral codes for clients</li>
                  <li>• Sending encouragement messages</li>
                  <li>• Accessing clinician dashboard features</li>
                  <li>• Receiving client milestone notifications</li>
                </ul>
                <p className="text-gray-700 mt-4 font-medium">
                  You must acknowledge these guidelines to use the platform as a clinician.
                </p>
              </div>

              <div className="flex gap-3">
                <button
                  onClick={cancelDecline}
                  className="flex-1 bg-gray-100 text-gray-700 py-3 px-4 rounded-lg font-medium hover:bg-gray-200 transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={confirmDecline}
                  className="flex-1 bg-red-600 text-white py-3 px-4 rounded-lg font-medium hover:bg-red-700 transition-colors"
                >
                  Decline and Logout
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ClinicianAcknowledgement;
